import { TZDate } from "@date-fns/tz";
import moment, { Moment } from "moment-timezone";
import { formatInTimeZone, fromZonedTime, toZonedTime } from "date-fns-tz";
import {
  startOfDay,
  startOfWeek,
  endOfDay,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subDays,
  addDays,
  addWeeks,
  addMonths,
  parseISO,
  format,
  parse,
  isSameDay,
  isValid,
} from "date-fns";

type AllowedTZDateInput = string | number | Date;
type NullableTZDateInput = AllowedTZDateInput | null | undefined;

const isAllowedTZDateInput = (
  date: NullableTZDateInput
): date is AllowedTZDateInput => date !== undefined && date !== null;

const createTZDateInstance = (
  value: AllowedTZDateInput,
  timezone?: string | null
) => {
  if (typeof value === "string") {
    return timezone ? new TZDate(value, timezone) : new TZDate(value);
  }

  if (typeof value === "number") {
    return timezone ? new TZDate(value, timezone) : new TZDate(value);
  }

  return timezone ? new TZDate(value, timezone) : new TZDate(value);
};

/**
 * Safely creates a TZDate from any supported input value and timezone.
 * Returns null when the input cannot be parsed into a finite date instance.
 */
export const getSafeTZDate = (
  date: NullableTZDateInput,
  timezone?: string | null
): TZDate | null => {
  if (!isAllowedTZDateInput(date)) {
    return null;
  }

  try {
    const tzDate = createTZDateInstance(date, timezone);

    return Number.isFinite(tzDate.valueOf()) ? tzDate : null;
  } catch {
    return null;
  }
};

export const getToday = (timezone = "UTC"): Date => {
  const zoned = toZonedTime(new Date(), timezone);
  return startOfDay(zoned);
};

export const getYesterday = (timezone = "UTC"): Date => {
  const zoned = toZonedTime(new Date(), timezone);
  const yesterday = subDays(zoned, 1);
  return startOfDay(yesterday);
};

export const getLastWeekend = (
  timezone = "UTC"
): { startDate: Date; endDate: Date } => {
  const now = toZonedTime(new Date(), timezone);
  // Moment’s `.startOf("week").subtract(2, "days")` → Friday of the previous week
  const startOfCurrentWeek = startOfWeek(now, { weekStartsOn: 0 }); // Sunday start
  const startDate = subDays(startOfCurrentWeek, 2); // Friday
  const endDate = endOfDay(startOfCurrentWeek); // Saturday
  return { startDate, endDate };
};

export const getLastWeek = (
  timezone = "UTC"
): { startDate: Date; endDate: Date } => {
  const now = toZonedTime(new Date(), timezone);
  const startOfCurrentWeek = startOfWeek(now, { weekStartsOn: 0 });
  const startDate = subDays(startOfCurrentWeek, 7); // previous Sunday
  const endDate = endOfDay(subDays(startOfCurrentWeek, 1)); // previous Saturday
  return { startDate, endDate };
};

export const getThisMonth = (
  timezone = "UTC"
): { startDate: Moment; endDate: Moment } => {
  const startDate = moment().tz(timezone).startOf("month");
  const endDate = moment().tz(timezone).endOf("month");
  return { startDate, endDate };
};
export const getLastMonth = (
  timezone = "UTC"
): { startDate: Moment; endDate: Moment } => {
  const startDate = moment()
    .tz(timezone)
    .startOf("month")
    .subtract(1, "months");
  const endDate = moment()
    .tz(timezone)
    .startOf("month")
    .subtract(1, "days")
    .endOf("day");
  return { startDate, endDate };
};

export const getTodayStartAndEndDate = (
  timezone?: string
): { startDate: Date; endDate: Date } => {
  const tz = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const now = toZonedTime(new Date(), tz);
  return {
    startDate: startOfDay(now),
    endDate: endOfDay(now),
  };
};

export const getTomorrowStartAndEndDate = (
  timezone?: string
): { startDate: Date; endDate: Date } => {
  const tz = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const now = toZonedTime(new Date(), tz);
  const tomorrow = addDays(now, 1);
  return {
    startDate: startOfDay(tomorrow),
    endDate: endOfDay(tomorrow),
  };
};

export const getThisWeekendStartAndEndDate = (
  timezone?: string
): { startDate: Date; endDate: Date } => {
  const tz = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const now = toZonedTime(new Date(), tz);
  const nextWeek = addWeeks(now, 1);
  const startOfNextWeek = startOfWeek(nextWeek, { weekStartsOn: 1 }); // ISO week starts Monday
  const startDate = subDays(startOfNextWeek, 2); // Saturday
  const endDate = endOfDay(subDays(startOfNextWeek, 1)); // Sunday
  return { startDate, endDate };
};

export const getNextWeekStartAndEndDate = (
  timezone?: string
): { startDate: Date; endDate: Date } => {
  const tz = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const now = toZonedTime(new Date(), tz);
  const nextWeek = addWeeks(now, 1);
  const startDate = startOfWeek(nextWeek, { weekStartsOn: 1 }); // ISO week starts Monday
  const endDate = endOfWeek(nextWeek, { weekStartsOn: 1 });
  return { startDate, endDate };
};

export const getThisMonthStartAndEndDate = (
  timezone?: string
): { startDate: Date; endDate: Date } => {
  const tz = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const now = toZonedTime(new Date(), tz);
  return {
    startDate: startOfMonth(now),
    endDate: endOfMonth(now),
  };
};

export const getNextMonthStartAndEndDate = (
  timezone?: string
): { startDate: Date; endDate: Date } => {
  const tz = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const now = toZonedTime(new Date(), tz);
  const nextMonth = addMonths(now, 1);
  return {
    startDate: startOfMonth(nextMonth),
    endDate: endOfMonth(nextMonth),
  };
};

/**
 * Checks if two dates are on the same day.
 * Wrapper around date-fns isSameDay function.
 * @param dateLeft - The first date to compare
 * @param dateRight - The second date to compare
 * @returns True if the dates are on the same day
 */
export const areDatesOnSameDay = (dateLeft: Date, dateRight: Date): boolean => {
  return isSameDay(dateLeft, dateRight);
};

const getMomentFormattedDate = ({
  date,
  timezone,
  format,
}: {
  date: string | Moment | Date | undefined;
  timezone: string | null;
  format: string;
}) => {
  const dateToFormat = date ? moment(date) : moment();
  return (
    dateToFormat
      // if timezone is provided, use it, otherwise use the browser's timezone
      .tz(timezone || moment.tz.guess())
      .format(format)
  );
};

const getDateFnsFormatedDate = ({
  date,
  timezone,
  format,
}: {
  date: TZDate;
  timezone: string | null;
  format: string;
}) => {
  const resolvedTimezone =
    timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  // use formatInTimeZone from date-fns-tz to get timezone abbreviation since date-fns only returns the offset
  return formatInTimeZone(date, resolvedTimezone, format);
};

/**
 * Returns a formatted date string based on our common usage
 * of displaying event and ticket type dates.
 * Can be used to display any other date
 *
 * TODO: stop using moment
 */
export const getFormattedDate = (
  options: {
    date?: string | Moment | Date | TZDate;
    timezone: string | null;
    format?: string;
    displayTime?: boolean;
    timeFormat?: string;
    displayTimezone?: boolean;
  } = { timezone: null } // Explicitly use browser timezone if timezone not provided (i.e null)
) => {
  const { date, timezone, format, displayTime, timeFormat, displayTimezone } =
    options;

  const isTZDate = date instanceof TZDate;
  const defaultFormat = isTZDate ? "MMMM d, yyyy" : "MMMM D, YYYY";
  const defaultTimeFormat = isTZDate ? "h:mm a" : "h:mm A";

  // Default to long date format
  let dateFormat = format || defaultFormat;

  // Add time format
  if (displayTime) {
    dateFormat = `${dateFormat} ${timeFormat || defaultTimeFormat}`;
  }

  // Usually will want to display the timezone if we are displaying the time
  const shouldDisplayTimezone = displayTimezone ?? displayTime;
  if (shouldDisplayTimezone) {
    dateFormat = `${dateFormat} zz`;
  }

  if (isTZDate) {
    return getDateFnsFormatedDate({ date, timezone, format: dateFormat });
  }
  return getMomentFormattedDate({ date, timezone, format: dateFormat });
};

/**
 * Takes a UTC datetime string, converts it to the specified timezone,
 * and returns the time formatted in 12-hour format (e.g., "11:30 AM").
 * @param utcDateString - The date/time in UTC format.
 * @param timezone - The venue timezone (e.g., "America/New_York").
 * @returns The time formatted as "h:mm a" (e.g., "12:00 AM").
 */
export const getDisplayTimeFromUTC = (
  utcDateString: string | undefined,
  timezone: string
): string => {
  if (!utcDateString) return "12:00 AM";

  // Parse the UTC date string
  const utcDate = parseISO(utcDateString);

  // Format as 12-hour time
  return formatInTimeZone(utcDate, timezone, "h:mm a");
};

/**
 * Combines a date object (Date) and a time string (e.g., "11:30 AM")
 * in a specified timezone, and returns the combined datetime as a UTC string.
 * @param date - The date part as a Date object.
 * @param timeString - The time part as a 12-hour string (e.g., "11:30 AM").
 * @param timezone - The venue timezone (e.g., "America/New_York").
 * @returns The combined datetime formatted as an ISO-8601 UTC string.
 */
export const combineDateTimeToUTC = (
  date: Date,
  timeString: string,
  timezone: string
): string => {
  // Parse the time string (e.g., "11:30 AM")
  const match = timeString.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);

  // If the time string is invalid, just return the date at midnight UTC
  if (!match) return formatInTimeZone(date, "UTC", "yyyy-MM-dd'T'HH:mm'Z'");

  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const meridiem = match[3]?.toUpperCase();

  // Convert 12-hour to 24-hour format
  if (meridiem === "PM" && hours !== 12) {
    hours += 12;
  } else if (meridiem === "AM" && hours === 12) {
    hours = 0;
  }

  // Get date string in YYYY-MM-DD format
  const dateStr = format(date, "yyyy-MM-dd");
  // Format the time part (e.g., "11:30" or "15:45").
  const localTimeString = `${String(hours).padStart(2, "0")}:${String(
    minutes
  ).padStart(2, "0")}`;
  // Combine into a single local string (e.g., "2025-06-15 11:30").
  const combinedLocalString = `${dateStr} ${localTimeString}`;
  // Convert the local string into a standard Date object.
  const parsedLocalDate = parse(
    combinedLocalString,
    "yyyy-MM-dd HH:mm",
    new Date()
  );
  // "Treat the time in 'parsedLocalDate' as the local time in the 'timezone' (e.g., Calgary),
  // and calculate the true, equivalent UTC moment."
  // (e.g., 11:30 AM MDT -> 17:30Z)
  const utcMoment = fromZonedTime(parsedLocalDate, timezone);

  // Format as UTC
  return formatInTimeZone(utcMoment, "UTC", "yyyy-MM-dd'T'HH:mm'Z'");
};

type ISODateInput = Date | TZDate | string | number | null | undefined;

/**
 * Formats a date-like value into a YYYY-MM-DD string that matches HTML date inputs.
 * Returns an empty string when the value cannot be converted into a valid date.
 */
export const formatDateToISODateString = (date?: ISODateInput): string => {
  if (!date) return "";
  const dateValue = date instanceof Date ? date : new Date(date);
  if (!isValid(dateValue)) return "";
  return format(dateValue, "yyyy-MM-dd");
};
